import IconButton, { type IconButtonProps } from "@/components/ui/icon-button";
import { MenuTrigger } from "@/components/ui/menu";
import { cn } from "@/lib/utils";
import { DotsThreeIcon } from "@phosphor-icons/react";
import { type VariantProps, cva } from "class-variance-authority";
import type { FC, ReactNode } from "react";

const segmentListItemVariants = cva(
  "flex items-center justify-between hover:bg-slate-300/10 rounded-xs cursor-pointer transition-colors",
  {
    variants: {
      size: {
        sm: "text-xxs",
        md: "text-xs",
        lg: "text-sm",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

interface SegmentListItemProps
  extends VariantProps<typeof segmentListItemVariants> {
  color: string;
  label: string;
  className?: string;
  isHidden?: boolean;
  children?: ReactNode;
  secondaryAction?: IconButtonProps;
}

const SegmentListItem: FC<SegmentListItemProps> = ({
  color,
  label,
  size = "md",
  className,
  children,
  secondaryAction,
  isHidden = false,
}) => {
  // Check if it's a hex color like "#50d71e" or "#fff"
  const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(color);

  // Make the dot size scale only for large size
  const dotSize = size === "lg" ? "w-3 h-3" : "w-2 h-2";
  return (
    <div
      className={cn(segmentListItemVariants({ size }), className)}
      aria-label={"segment-list-item"}
    >
      <div className="flex items-center space-x-1.5 min-w-0 flex-1">
        <span
          className={cn(
            dotSize,
            "rounded-full shrink-0",
            isHidden ? "bg-gray-200" : !isHex && color,
          )}
          style={
            isHidden
              ? undefined
              : isHex
                ? { backgroundColor: color }
                : undefined
          }
          role="presentation"
          aria-label="Color indicator"
        />
        <span
          className={cn(
            segmentListItemVariants({ size }),
            isHidden ? "text-gray-500" : "text-gray-900",
            "overflow-hidden text-ellipsis whitespace-nowrap block",
          )}
        >
          {label}
        </span>
      </div>
      <div
        className="flex items-center space-x-2 shrink-0"
        role="toolbar"
        aria-label="Segment Actions"
      >
        {secondaryAction && <IconButton {...secondaryAction} />}
        {children && (
          <MenuTrigger>
            <IconButton
              icon={<DotsThreeIcon size={20} />}
              aria-label={"More Options"}
              variant={"ghost"}
              size={"md"}
              tooltipLabel={"More Options"}
            />
            {children}
          </MenuTrigger>
        )}
      </div>
    </div>
  );
};

export default SegmentListItem;
